import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { gameState } from '../state/gameState.js';

// DEV-only tool: tune each decoration's display base size against Natalka for scale,
// then export the size map to the clipboard. Reach it locally at /?sizelab (no login).
// ←/→ switch item · ↑/↓ or mouse wheel scale · E export.
const DECOR_IDS = [
  'roslina', 'balony', 'girlanda', 'dywan', 'plakat', 'zyrandol',
  'lawka', 'skrzynka_kwiaty', 'latarnia', 'posag', 'fontanna',
];
const GROUND_Y = 600;
const NATALKA_W = 160, NATALKA_H = 240;   // Natalka's in-game display size
const DECOR_X = 760;

export class SizeLabScene extends Phaser.Scene {
  constructor() { super('SizeLabScene'); }

  async create() {
    this.cameras.main.setBackgroundColor('#2b2b3a');
    this.sizes = Object.fromEntries(DECOR_IDS.map((id) => [id, 120]));   // base box (px), default 120
    this.idx = 0;

    this.add.rectangle(GAME_WIDTH / 2, GROUND_Y, GAME_WIDTH, 3, 0x6688aa).setOrigin(0.5);

    // Natalka reference (feet on the ground line, in-game size).
    const ntex = this.textures.exists('natalka_base') ? 'natalka_base'
      : (this.textures.exists('natalka') ? 'natalka' : 'plot_empty');
    this.add.image(220, GROUND_Y - NATALKA_H / 2, ntex).setDisplaySize(NATALKA_W, NATALKA_H);
    this.add.text(220, GROUND_Y + 10, `Natalka (${NATALKA_W}×${NATALKA_H})`, {
      fontFamily: 'system-ui', fontSize: '16px', color: '#fff',
    }).setOrigin(0.5, 0);

    // Current decoration — bottom anchored on the ground line so baselines align.
    this.decor = this.add.image(DECOR_X, GROUND_Y, 'plot_empty').setOrigin(0.5, 1);

    this.caption = this.add.text(GAME_WIDTH / 2, 26, '', {
      fontFamily: 'system-ui', fontSize: '24px', color: '#ffd166', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.sizeText = this.add.text(DECOR_X, GROUND_Y + 10, '', {
      fontFamily: 'system-ui', fontSize: '18px', color: '#fff',
    }).setOrigin(0.5, 0);
    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 26,
      '←/→ przedmiot · ↑/↓ lub kółko = skaluj · E = eksportuj rozmiary do schowka', {
        fontFamily: 'system-ui', fontSize: '16px', color: '#bbb',
      }).setOrigin(0.5);

    this.input.keyboard.on('keydown', (e) => this._onKey(e));
    this.input.on('wheel', (_p, _o, _dx, dy) => this._scale(dy > 0 ? -5 : 5));

    this._show();

    // Seed from the live catalog (your last-applied sizes) — needs a logged-in cookie.
    try {
      await gameState.refresh();
      const decos = gameState.raw?.catalog?.decorations ?? {};
      for (const id of DECOR_IDS) if (decos[id]?.size) this.sizes[id] = decos[id].size;
      this._show();
    } catch { /* not logged in — keep defaults */ }
  }

  _onKey(e) {
    const k = e.key;
    if (k === 'ArrowLeft')  { this.idx = (this.idx - 1 + DECOR_IDS.length) % DECOR_IDS.length; this._show(); }
    else if (k === 'ArrowRight') { this.idx = (this.idx + 1) % DECOR_IDS.length; this._show(); }
    else if (k === 'ArrowUp')   this._scale(5);
    else if (k === 'ArrowDown') this._scale(-5);
    else if (k === 'e' || k === 'E') this._export();
  }

  _scale(d) {
    const id = DECOR_IDS[this.idx];
    this.sizes[id] = Phaser.Math.Clamp(this.sizes[id] + d, 30, 600);
    this._applySize();
  }

  _show() {
    const id = DECOR_IDS[this.idx];
    const tex = `decoration_${id}`;
    this.decor.setTexture(this.textures.exists(tex) ? tex : 'plot_empty');
    this._applySize();
  }

  _applySize() {
    const id = DECOR_IDS[this.idx];
    const base = this.sizes[id];
    const w = this.decor.width || base, h = this.decor.height || base;   // natural texture px
    const scale = base / Math.max(w, h);
    this.decor.setDisplaySize(w * scale, h * scale).setPosition(DECOR_X, GROUND_Y);
    this.caption.setText(`${id}   (${this.idx + 1}/${DECOR_IDS.length})`);
    this.sizeText.setText(`base ${base}px`);
  }

  _export() {
    const text = JSON.stringify(this.sizes, null, 2);
    navigator.clipboard?.writeText(text).catch(() => {});
    const t = this.add.text(GAME_WIDTH / 2, 68, '📋 Skopiowano rozmiary do schowka!', {
      fontFamily: 'system-ui', fontSize: '18px', color: '#9fe89f', backgroundColor: '#000000aa',
      padding: { x: 8, y: 4 },
    }).setOrigin(0.5);
    this.time.delayedCall(1500, () => t.destroy());
  }
}
